import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function OrderDetailsDialog({ 
  open, onOpenChange, selectedOrder, projects, user, orderComment, setOrderComment,
  onUpdateOrderStatus, onCancelOrder, onDeleteOrder, onExportOrder, onAddOrderComment,
  onShowDeliveryPhoto, onShowAddCost, setCostForm
}) {
  return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>Material Order Details</DialogTitle>
              {selectedOrder && (
                <Button
                  onClick={() => onExportOrder(selectedOrder.id)}
                  variant="outline"
                  size="sm"
                  disabled={loading}
                  data-testid="export-order-btn"
                >
                  📧 Export for Email
                </Button>
              )}
            </div>
          </DialogHeader>
          {selectedOrder && (
            <div className="order-details-content">
              {/* Order ID */}
              <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                <Label className="text-sm font-semibold text-blue-800">Order ID</Label>
                <p className="font-mono text-lg text-blue-900">{selectedOrder.id}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded">
                <div>
                  <Label className="text-sm text-gray-600">Order Date</Label>
                  <p className="font-semibold">{new Date(selectedOrder.order_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Order Type</Label>
                  <p className="font-semibold capitalize">{selectedOrder.order_type}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Delivery Date</Label>
                  <p className="font-semibold">
                    {selectedOrder.delivery_date ? new Date(selectedOrder.delivery_date).toLocaleDateString() : 'Not specified'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Status</Label>
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      selectedOrder.status === 'delivered' || selectedOrder.status === 'complete' ? 'success' : 
                      selectedOrder.status === 'ordered' ? 'default' : 
                      selectedOrder.status === 'cancelled' ? 'destructive' :
                      'warning'
                    }>
                      {selectedOrder.status}
                    </Badge>
                    {(['purchaser', 'accounts', 'admin', 'company_admin'].includes(user.role)) && (
                      <div className="flex gap-1">
                        {selectedOrder.status === 'pending' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => onUpdateOrderStatus(selectedOrder.id, 'ordered')}
                            disabled={loading}
                          >
                            Mark as Ordered
                          </Button>
                        )}
                        {selectedOrder.status === 'ordered' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              onShowDeliveryPhoto(true);
                            }}
                            disabled={loading}
                          >
                            📷 Mark as Received at Site
                          </Button>
                        )}
                        {selectedOrder.status === 'received_at_site' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => onUpdateOrderStatus(selectedOrder.id, 'complete')}
                            disabled={loading}
                          >
                            Mark as Complete
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm text-gray-600">Project</Label>
                  <p className="font-semibold">
                    {projects.find(p => p.id === selectedOrder.project_id)?.name || 'Unknown'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {projects.find(p => p.id === selectedOrder.project_id)?.address || ''}
                  </p>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm text-gray-600">Created By</Label>
                  {(() => {
                    const creator = employees.find(e => e.user_id === selectedOrder.ordered_by);
                    const phone = creator?.user?.phone || creator?.phone;
                    const name = creator?.user?.name || creator?.name || 'Unknown';
                    return (
                      <div>
                        <p className="font-semibold">
                          {name}
                          {phone && <span className="text-sm font-normal text-gray-600 ml-2">({phone})</span>}
                        </p>
                        {!phone && <p className="text-sm text-gray-400">No contact number</p>}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Cost Information Section - Only visible to managers and above, hidden from employees */}
              {!['employee', 'subcontractor'].includes(user.role) && (
              <div className="mb-6 p-4 bg-yellow-50 rounded border border-yellow-200">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-yellow-900">Cost Details</h3>
                  {['purchaser', 'accounts', 'company_admin', 'accounts'].includes(user.role) && (
                    <Button
                      onClick={() => {
                        setCostForm({
                          order_cost: selectedOrder.order_cost?.toString() || '',
                          delivery_charges: selectedOrder.delivery_charges?.toString() || ''
                        });
                        onShowAddCost(true);
                      }}
                      variant="outline"
                      size="sm"
                    >
                      {selectedOrder.order_cost ? 'Edit Cost' : 'Add Cost'}
                    </Button>
                  )}
                </div>
                
                {selectedOrder.order_cost !== null && selectedOrder.order_cost !== undefined ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm text-yellow-700">Order Cost (without HST)</Label>
                      <p className="text-lg font-bold text-yellow-900">
                        ${selectedOrder.order_cost?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-yellow-700">Delivery Charges</Label>
                      <p className="text-lg font-bold text-yellow-900">
                        ${selectedOrder.delivery_charges?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-yellow-700">Total Cost</Label>
                      <p className="text-xl font-bold text-yellow-900">
                        ${selectedOrder.total_cost?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-yellow-700">No cost information added yet</p>
                    {['purchaser', 'accounts', 'company_admin', 'accounts'].includes(user.role) && (
                      <Button
                        onClick={() => {
                          setCostForm({
                            order_cost: '',
                            delivery_charges: ''
                          });
                          onShowAddCost(true);
                        }}
                        size="sm"
                      >
                        Add Cost
                      </Button>
                    )}
                  </div>
                )}
              </div>
              )}

              {selectedOrder.insulation_items && (
                <div className="insulation-details">
                  <h3 className="text-lg font-semibold mb-4">Order Items</h3>
                  
                  {/* Pipe Insulation */}
                  {selectedOrder.insulation_items.pipe_insulation && selectedOrder.insulation_items.pipe_insulation.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">Pipe Insulation</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2 border">Type</th>
                            <th className="text-left p-2 border">Size</th>
                            <th className="text-left p-2 border">Thickness</th>
                            <th className="text-right p-2 border">Quantity (ft)</th>
                            <th className="text-right p-2 border">Pieces</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.pipe_insulation.map((item, idx) => (
                            <tr key={idx} className="border">
                              <td className="p-2 border capitalize">{item.pipe_type || '-'}</td>
                              <td className="p-2 border">{item.size || '-'}</td>
                              <td className="p-2 border">{item.thickness || '-'}</td>
                              <td className="text-right p-2 border">{item.quantity_ft || '-'}</td>
                              <td className="text-right p-2 border">{item.quantity_pieces || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* PVC Sheets */}
                  {selectedOrder.insulation_items.pvc_sheets && selectedOrder.insulation_items.pvc_sheets.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">PVC Sheets</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2 border">PVC Number</th>
                            <th className="text-right p-2 border">Quantity (sheets)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.pvc_sheets.map((item, idx) => (
                            <tr key={idx} className="border">
                              <td className="p-2 border">{item.pvc_number}</td>
                              <td className="text-right p-2 border">{item.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* PVC Rolls */}
                  {selectedOrder.insulation_items.pvc_rolls && selectedOrder.insulation_items.pvc_rolls.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">PVC Rolls</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-right p-2 border">Number of Rolls</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.pvc_rolls.map((item, idx) => (
                            <tr key={idx} className="border">
                              <td className="text-right p-2 border">{item.rolls}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Duct Insulation */}
                  {selectedOrder.insulation_items.duct_insulation && selectedOrder.insulation_items.duct_insulation.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">Duct Insulation</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2 border">Type</th>
                            <th className="text-left p-2 border">Thickness</th>
                            <th className="text-right p-2 border">Quantity (sheets)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.duct_insulation.map((item, idx) => (
                            <tr key={idx} className="border">
                              <td className="p-2 border capitalize">{item.duct_type ? item.duct_type.replace('_', ' ') : '-'}</td>
                              <td className="p-2 border">{item.thickness || '-'}</td>
                              <td className="text-right p-2 border">{item.quantity || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Jacketing Items */}
                  {selectedOrder.insulation_items.jacketing_items && selectedOrder.insulation_items.jacketing_items.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">Jacketing Materials</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2 border">Type</th>
                            <th className="text-right p-2 border">Rolls</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.jacketing_items.map((item, idx) => (
                            <tr key={idx} className="border">
                              <td className="p-2 border">{item.type}</td>
                              <td className="text-right p-2 border">{item.rolls}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Tape Items */}
                  {selectedOrder.insulation_items.tape_items && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-md mb-2">Tape & Fasteners</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2 border">Item</th>
                            <th className="text-right p-2 border">Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.insulation_items.tape_items.silver_tape && (
                            <tr className="border">
                              <td className="p-2 border">Silver Tape</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.silver_tape} rolls</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.aerp_tape && (
                            <tr className="border">
                              <td className="p-2 border">Aero Tape</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.aerp_tape} rolls</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.vinyl_white_tape && (
                            <tr className="border">
                              <td className="p-2 border">Vinyl White Tape</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.vinyl_white_tape} rolls</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.corner_beads && (
                            <tr className="border">
                              <td className="p-2 border">Corner Beads</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.corner_beads} packets</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.canvas_lagging && (
                            <tr className="border">
                              <td className="p-2 border">Canvas Lagging</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.canvas_lagging} litres</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.rubber_glue && (
                            <tr className="border">
                              <td className="p-2 border">Rubber Glue</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.rubber_glue} litres</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.silicon_sealant && (
                            <tr className="border">
                              <td className="p-2 border">Silicon Sealant Outdoor</td>
                              <td className="text-right p-2 border">{selectedOrder.insulation_items.tape_items.silicon_sealant} tubes</td>
                            </tr>
                          )}
                          {selectedOrder.insulation_items.tape_items.cup_head_pins && selectedOrder.insulation_items.tape_items.cup_head_pins.length > 0 && (
                            selectedOrder.insulation_items.tape_items.cup_head_pins.map((pin, idx) => (
                              <tr key={idx} className="border">
                                <td className="p-2 border">Cup Head Pins ({pin.size})</td>
                                <td className="text-right p-2 border">{pin.quantity} boxes</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {selectedOrder.notes && (
                <div className="mt-4 p-3 bg-gray-50 rounded">
                  <Label className="text-sm font-semibold">Notes</Label>
                  <p className="mt-1 text-sm">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Communication Section */}
              <div className="mt-6 border-t pt-4">
                <h4 className="font-semibold text-md mb-3">Communication</h4>
                
                {/* Display existing comments */}
                {selectedOrder.comments && selectedOrder.comments.length > 0 && (
                  <div className="mb-4 max-h-64 overflow-y-auto space-y-2">
                    {selectedOrder.comments.map((comment, idx) => {
                      const commenter = employees.find(e => e.user_id === comment.user_id);
                      return (
                        <div key={idx} className="p-3 bg-blue-50 rounded border border-blue-200">
                          <div className="flex justify-between items-start mb-1">
                            <p className="font-semibold text-sm">{commenter?.user?.name || commenter?.name || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(comment.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <p className="text-sm text-gray-700">{comment.message}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add new comment */}
                <div className="space-y-2">
                  <Label className="text-sm">Add Comment / Report Issue</Label>
                  <Textarea
                    value={orderComment}
                    onChange={(e) => setOrderComment(e.target.value)}
                    placeholder="e.g., Item not available from supplier, need alternative..."
                    rows={3}
                  />
                  <Button 
                    onClick={() => onAddOrderComment(selectedOrder.id)}
                    disabled={loading || !orderComment.trim()}
                    size="sm"
                  >
                    Send Message
                  </Button>
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <div className="flex gap-2">
                  {/* Cancel Order - available to accounts and purchaser */}
                  {(['purchaser', 'accounts', 'company_admin'].includes(user.role)) && 
                   selectedOrder.status !== 'cancelled' && 
                   selectedOrder.status !== 'complete' && (
                    <Button 
                      onClick={() => onCancelOrder(selectedOrder.id)}
                      variant="outline"
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      disabled={loading}
                      data-testid="cancel-order-btn"
                    >
                      Cancel Order
                    </Button>
                  )}
                  
                  {/* Delete Order - only company_admin */}
                  {user.role === 'company_admin' && (
                    <Button 
                      onClick={() => onDeleteOrder(selectedOrder.id)}
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      disabled={loading}
                      data-testid="delete-order-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete Order
                    </Button>
                  )}
                </div>
                <Button onClick={() => {
                  onOpenChange(false);
                  setOrderComment('');
                }}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

  );
}

export default OrderDetailsDialog;
